import { toLocationsForMlDispatch } from './hubDispatchFilter.js';
import { hasInternalHubDispatchControls } from './networkMode.js';
import {
  buildLocationHubParentMap,
  crossingSelectionFromRoute,
  mlFormFieldsFromMatchedRoute,
  routeDestinationSequence,
  routeMatchesMlFromTo,
  travelIndexOnRoute,
} from './routeDisplay.js';

export const crossingDestinationIds = (route) => {
  const ids = [];
  const seen = new Set();
  (route?.crossing_stops || []).forEach((stop) => {
    const destIds = [
      ...(stop.destination_hub_ids || []),
      ...((stop.destinations || []).map((dest) => dest.id).filter((id) => id != null)),
    ];
    destIds.forEach((raw) => {
      const id = Number(raw);
      if (Number.isNaN(id) || seen.has(id)) return;
      seen.add(id);
      ids.push(id);
    });
  });
  return ids;
};

const idSetKey = (ids) => (ids || []).map((id) => String(id)).sort().join(',');

const crossingKey = (stops) => (stops || [])
  .map((stop) => `${stop.hub_id}:${(stop.destination_hub_ids || []).join(',')}`)
  .join('|');

const maxTravelIndex = (route, hubIds, parentMap) => {
  let maxFrom = -1;
  (hubIds || []).forEach((id) => {
    const idx = travelIndexOnRoute(route, id, parentMap);
    if (idx != null) maxFrom = Math.max(maxFrom, idx);
  });
  return maxFrom;
};

/** Keep stops that are strictly ahead of From, and always include forward crossing cities. */
export const mergeForwardToIds = (route, fromIds, toIds, parentMap) => {
  const maxFrom = maxTravelIndex(route, fromIds, parentMap);
  const isForward = (raw) => {
    const idx = travelIndexOnRoute(route, raw, parentMap);
    return idx != null && idx > maxFrom;
  };
  const seen = new Set();
  const out = [];
  const push = (raw) => {
    const id = Number(raw);
    if (Number.isNaN(id) || seen.has(id) || !isForward(id)) return;
    seen.add(id);
    out.push(id);
  };
  (toIds || []).forEach(push);
  crossingDestinationIds(route).forEach(push);
  return out;
};

/**
 * From may already be a via stop (Guntur on Bangalore–Vijayawada).
 * To becomes every later via stop, the final hub, and crossing cities ahead of From.
 * A From/To the user already chose is kept, with crossing cities added.
 */
export const dispatchLocationsForRoute = (route, prevFrom, prevTo, parentMap) => {
  const fromOnRoute = (prevFrom || []).length > 0
    && (prevFrom || []).every((id) => travelIndexOnRoute(route, id, parentMap) != null);
  const fromIds = fromOnRoute
    ? [...prevFrom]
    : (route?.from_location_hub_id != null ? [route.from_location_hub_id] : []);

  const maxFrom = maxTravelIndex(route, fromIds, parentMap);
  const isForward = (raw) => {
    const idx = travelIndexOnRoute(route, raw, parentMap);
    return idx != null && idx > maxFrom;
  };
  const forwardUserTo = (prevTo || []).filter(isForward);
  const keepUserTo = fromOnRoute
    && forwardUserTo.length > 0
    && routeMatchesMlFromTo(route, fromIds, forwardUserTo, parentMap);

  if (keepUserTo) {
    return { fromIds, toIds: mergeForwardToIds(route, fromIds, prevTo, parentMap) };
  }

  const seen = new Set();
  const toIds = [];
  const push = (raw) => {
    const id = Number(raw);
    if (Number.isNaN(id) || seen.has(id) || !isForward(id)) return;
    seen.add(id);
    toIds.push(id);
  };
  routeDestinationSequence(route).forEach(push);
  crossingDestinationIds(route).forEach(push);
  return { fromIds, toIds };
};

/** Drop main-path stops the From hub cannot dispatch to. Crossing cities stay. */
export const finalizeDispatchToIds = (route, toIds, eligibleIdSet) => {
  if (!eligibleIdSet) return toIds || [];
  const crossing = new Set(crossingDestinationIds(route).map((id) => String(id)));
  return (toIds || []).filter(
    (id) => eligibleIdSet.has(String(id)) || crossing.has(String(id)),
  );
};

const eligibleToIdSet = (locations, fromIds, user) => new Set(
  toLocationsForMlDispatch(locations, fromIds, {
    enabled: hasInternalHubDispatchControls(user),
  }).map((loc) => String(loc.id || loc.hub_id)),
);

const withRouteCrossing = (prev, route) => {
  const picked = crossingSelectionFromRoute(route);
  return {
    ...prev,
    route_id: route.id,
    ...(picked.crossing_stops.length
      ? {
        crossing_stops: picked.crossing_stops,
        crossing_location_hub_ids: picked.crossing_location_hub_ids,
        crossing_destination_hub_ids: picked.crossing_destination_hub_ids,
        crossingStopsFromRoute: true,
      }
      : {
        crossing_stops: [],
        crossing_location_hub_ids: [],
        crossing_destination_hub_ids: [],
        crossingStopsFromRoute: false,
      }),
  };
};

/** Apply a saved route onto ML form state. A From stop already on the route is kept. */
export const applySelectedRouteToMlForm = (
  route,
  prev,
  { locations, user, parentMap: parentMapIn },
) => {
  if (!route) return prev;
  const parentMap = parentMapIn || buildLocationHubParentMap(locations);
  const aligned = dispatchLocationsForRoute(
    route,
    prev.from_location_ids,
    prev.to_location_ids,
    parentMap,
  );
  const toIds = finalizeDispatchToIds(
    route,
    aligned.toIds,
    aligned.fromIds.length ? eligibleToIdSet(locations, aligned.fromIds, user) : null,
  );
  return withRouteCrossing({
    ...prev,
    from_location_ids: aligned.fromIds,
    to_location_ids: toIds,
  }, route);
};

/**
 * After From/To change, keep the route when travel still moves forward.
 * Crossing cities are added. Stops behind the new From are removed.
 */
export const syncMlRouteSelection = (prev, { matched, routes, parentMap }) => {
  const from = prev.from_location_ids || [];
  const to = prev.to_location_ids || [];
  if (!from.length || !to.length) {
    if (!prev.route_id) return prev;
    return mlFormFieldsFromMatchedRoute(prev, null);
  }

  const current = (routes || []).find((item) => String(item.id) === String(prev.route_id));
  const route = matched?.id ? matched : current;
  if (!route) return prev;

  const fromOnRoute = from.every((id) => travelIndexOnRoute(route, id, parentMap) != null);
  if (!fromOnRoute) {
    if (matched?.id) return mlFormFieldsFromMatchedRoute(prev, matched);
    if (current && routeMatchesMlFromTo(current, from, to, parentMap)) return prev;
    return prev.route_id ? mlFormFieldsFromMatchedRoute(prev, null) : prev;
  }

  const nextTo = mergeForwardToIds(route, from, to, parentMap);
  if (!nextTo.length) {
    return prev.route_id ? mlFormFieldsFromMatchedRoute(prev, null) : prev;
  }

  const draft = withRouteCrossing({ ...prev, to_location_ids: nextTo }, route);
  if (
    String(draft.route_id) === String(prev.route_id)
    && idSetKey(draft.to_location_ids) === idSetKey(prev.to_location_ids)
    && crossingKey(draft.crossing_stops) === crossingKey(prev.crossing_stops)
  ) {
    return prev;
  }
  return draft;
};
